import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { FiLayers, FiPlus, FiEdit2, FiTrash2, FiZap, FiX } from 'react-icons/fi';
import { apiGet, apiPost, apiPut, apiDelete } from '@/lib/api';
import { Card, CardContent } from '@/shared/components/ui/Card';
import { Button } from '@/shared/components/ui/Button';
import { Badge } from '@/shared/components/ui/Badge';
import { Skeleton } from '@/shared/components/ui/Skeleton';
import { Input } from '@/shared/components/ui/Input';
import { Select } from '@/shared/components/ui/Select';
import { FormField } from '@/shared/components/ui/FormField';
import { Drawer } from '@/shared/components/ui/Drawer';
import { toast } from 'sonner';

interface Plan {
  id: string;
  name: string;
  description?: string;
  creditsPerCycle?: number;
  price?: number | { monthly?: number; yearly?: number; currency?: string };
  features?: string[];
  activeSubscriptions?: number;
  mrr?: number;
  arr?: number;
  status: string;
  createdAt: string;
  updatedAt: string;
}

interface PlanFormData {
  name: string;
  description: string;
  creditsPerCycle: string;
  priceMonthly: string;
  priceYearly: string;
  currency: string;
  status: string;
  features: string[];
}

const emptyForm: PlanFormData = {
  name: '',
  description: '',
  creditsPerCycle: '',
  priceMonthly: '',
  priceYearly: '',
  currency: 'BRL',
  status: 'active',
  features: [],
};

export const PlansPage: React.FC = () => {
  const queryClient = useQueryClient();
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [editingPlan, setEditingPlan] = useState<Plan | null>(null);
  const [form, setForm] = useState<PlanFormData>(emptyForm);
  const [featureInput, setFeatureInput] = useState('');

  const { data: plans, isLoading } = useQuery({
    queryKey: ['admin-plans'],
    queryFn: () => apiGet<Plan[]>('/plans'),
  });

  const buildPayload = () => ({
    name: form.name.trim(),
    description: form.description.trim(),
    creditsPerCycle: parseInt(form.creditsPerCycle, 10) || 0,
    price: {
      monthly: parseFloat(form.priceMonthly) || 0,
      yearly: parseFloat(form.priceYearly) || 0,
      currency: form.currency || 'BRL',
    },
    features: form.features.length > 0 ? form.features : undefined,
    status: form.status as 'active' | 'inactive',
  });

  const createMutation = useMutation({
    mutationFn: (data: ReturnType<typeof buildPayload>) => apiPost('/plans', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-plans'] });
      closeDrawer();
      toast.success('Plano criado com sucesso');
    },
    onError: () => toast.error('Erro ao criar plano'),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: ReturnType<typeof buildPayload> }) =>
      apiPut(`/plans/${id}`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-plans'] });
      closeDrawer();
      toast.success('Plano atualizado com sucesso');
    },
    onError: () => toast.error('Erro ao atualizar plano'),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => apiDelete(`/plans/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-plans'] });
      toast.success('Plano removido com sucesso');
    },
    onError: () => toast.error('Erro ao remover plano'),
  });

  const openCreateDrawer = () => {
    setEditingPlan(null);
    setForm(emptyForm);
    setFeatureInput('');
    setIsDrawerOpen(true);
  };

  const closeDrawer = () => {
    setIsDrawerOpen(false);
    setEditingPlan(null);
    setForm(emptyForm);
    setFeatureInput('');
  };

  const getPlanPrice = (plan: Plan): { monthly: number; yearly: number; currency: string } => {
    if (plan.price && typeof plan.price === 'object') {
      return {
        monthly: plan.price.monthly ?? 0,
        yearly: plan.price.yearly ?? 0,
        currency: plan.price.currency ?? 'BRL',
      };
    }
    const legacy = typeof plan.price === 'number' ? plan.price : 0;
    return { monthly: legacy, yearly: 0, currency: 'BRL' };
  };

  const handleEdit = (plan: Plan) => {
    const price = getPlanPrice(plan);
    setEditingPlan(plan);
    setForm({
      name: plan.name,
      description: plan.description || '',
      creditsPerCycle: (plan.creditsPerCycle ?? '').toString(),
      priceMonthly: price.monthly.toString(),
      priceYearly: price.yearly.toString(),
      currency: price.currency,
      status: plan.status,
      features: plan.features ?? [],
    });
    setFeatureInput('');
    setIsDrawerOpen(true);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.name.trim()) return;
    if (!form.creditsPerCycle || parseInt(form.creditsPerCycle, 10) <= 0) {
      toast.error('Creditos por ciclo deve ser um numero positivo');
      return;
    }

    const payload = buildPayload();

    if (editingPlan) {
      updateMutation.mutate({ id: editingPlan.id, data: payload });
    } else {
      createMutation.mutate(payload);
    }
  };

  const handleDelete = (plan: Plan) => {
    if (confirm(`Deseja remover o plano "${plan.name}"?`)) {
      deleteMutation.mutate(plan.id);
    }
  };

  const handleAddFeature = () => {
    const trimmed = featureInput.trim();
    if (trimmed && !form.features.includes(trimmed)) {
      setForm(f => ({ ...f, features: [...f.features, trimmed] }));
      setFeatureInput('');
    }
  };

  const handleRemoveFeature = (feature: string) => {
    setForm(f => ({ ...f, features: f.features.filter(ft => ft !== feature) }));
  };

  const formatCurrency = (value?: number) => {
    if (value === undefined || value === null || isNaN(value)) return '-';
    return value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
  };

  const formatCredits = (value?: number) => {
    if (value === undefined || value === null || isNaN(value)) return '-';
    return new Intl.NumberFormat('pt-BR').format(value);
  };

  const isSaving = createMutation.isPending || updateMutation.isPending;

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-xl font-bold" style={{ color: 'var(--theme-card-text)' }}>Planos</h1>
          <p className="text-sm" style={{ color: 'var(--theme-muted-text)' }}>Gerencie os planos disponiveis no app</p>
        </div>
        <Button icon={FiPlus} onClick={openCreateDrawer}>
          Novo plano
        </Button>
      </div>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="p-6 space-y-4">
              {Array.from({ length: 3 }).map((_, i) => (
                <Skeleton key={i} height={20} />
              ))}
            </div>
          ) : !plans || plans.length === 0 ? (
            <div className="flex flex-col items-center text-center p-12">
              <div
                className="flex items-center justify-center w-12 h-12 rounded-xl mb-4"
                style={{ backgroundColor: 'var(--theme-primary-background)' }}
              >
                <FiLayers className="h-6 w-6" style={{ color: 'var(--theme-primary)' }} />
              </div>
              <h3 className="text-base font-semibold mb-1" style={{ color: 'var(--theme-card-text)' }}>
                Nenhum plano cadastrado
              </h3>
              <p className="text-sm max-w-sm" style={{ color: 'var(--theme-muted-text)' }}>
                Crie o primeiro plano para o seu app.
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr style={{ borderBottom: '1px solid var(--theme-card-border)' }}>
                    {['Nome', 'Creditos/ciclo', 'Mensal', 'Anual', 'Features', 'Assinaturas', 'MRR', 'ARR', 'Status', 'Acoes'].map((h) => (
                      <th
                        key={h}
                        className="text-left px-4 py-3 text-xs font-medium uppercase tracking-wider"
                        style={{ color: 'var(--theme-muted-text)' }}
                      >
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {plans.map((plan, index) => {
                    const price = getPlanPrice(plan);
                    return (
                      <tr
                        key={plan.id}
                        className={index > 0 ? 'border-t' : ''}
                        style={index > 0 ? { borderColor: 'var(--theme-card-border)' } : {}}
                      >
                        <td className="px-4 py-3" style={{ color: 'var(--theme-card-text)' }}>
                          <div>
                            <span className="font-medium">{plan.name}</span>
                            {plan.description && (
                              <p className="text-xs mt-0.5" style={{ color: 'var(--theme-muted-text)' }}>
                                {plan.description}
                              </p>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-3" style={{ color: 'var(--theme-card-text)' }}>
                          <div className="flex items-center gap-1">
                            <FiZap className="h-3.5 w-3.5" style={{ color: '#8b5cf6' }} />
                            <span>{formatCredits(plan.creditsPerCycle)}</span>
                          </div>
                        </td>
                        <td className="px-4 py-3" style={{ color: 'var(--theme-card-text)' }}>
                          {formatCurrency(price.monthly)}
                        </td>
                        <td className="px-4 py-3" style={{ color: 'var(--theme-card-text)' }}>
                          {formatCurrency(price.yearly)}
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex flex-wrap gap-1">
                            {plan.features && plan.features.length > 0 ? (
                              plan.features.slice(0, 3).map((f) => (
                                <span
                                  key={f}
                                  className="text-[10px] px-1.5 py-0.5 rounded-full"
                                  style={{
                                    backgroundColor: 'rgba(139, 92, 246, 0.1)',
                                    color: '#8b5cf6',
                                  }}
                                >
                                  {f}
                                </span>
                              ))
                            ) : (
                              <span style={{ color: 'var(--theme-muted-text)' }}>-</span>
                            )}
                            {plan.features && plan.features.length > 3 && (
                              <span
                                className="text-[10px] px-1.5 py-0.5 rounded-full"
                                style={{ color: 'var(--theme-muted-text)' }}
                              >
                                +{plan.features.length - 3}
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-3" style={{ color: 'var(--theme-card-text)' }}>
                          <span className="font-medium">{plan.activeSubscriptions ?? 0}</span>
                        </td>
                        <td className="px-4 py-3" style={{ color: 'var(--theme-card-text)' }}>
                          <span className="font-medium">{formatCurrency(plan.mrr)}</span>
                        </td>
                        <td className="px-4 py-3" style={{ color: 'var(--theme-card-text)' }}>
                          <span className="font-medium">{formatCurrency(plan.arr)}</span>
                        </td>
                        <td className="px-4 py-3">
                          <Badge variant={plan.status === 'active' ? 'success' : 'error'}>
                            {plan.status === 'active' ? 'Ativo' : 'Inativo'}
                          </Badge>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-1">
                            <button
                              onClick={() => handleEdit(plan)}
                              className="p-1.5 rounded-lg transition-colors"
                              style={{ color: 'var(--theme-muted-text)' }}
                              onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = 'var(--theme-button-ghost-hover)'; }}
                              onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
                              title="Editar"
                            >
                              <FiEdit2 className="h-4 w-4" />
                            </button>
                            <button
                              onClick={() => handleDelete(plan)}
                              className="p-1.5 rounded-lg transition-colors"
                              style={{ color: 'var(--theme-muted-text)' }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.backgroundColor = 'var(--theme-button-ghost-hover)';
                                e.currentTarget.style.color = 'var(--theme-error-color)';
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.backgroundColor = 'transparent';
                                e.currentTarget.style.color = 'var(--theme-muted-text)';
                              }}
                              title="Remover"
                            >
                              <FiTrash2 className="h-4 w-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Drawer */}
      <Drawer
        isOpen={isDrawerOpen}
        onClose={closeDrawer}
        title={editingPlan ? 'Editar plano' : 'Novo plano'}
        subtitle={editingPlan ? editingPlan.name : 'Preencha os dados do plano'}
        icon={<FiLayers className="h-5 w-5" />}
        disabled={isSaving}
        footer={
          <>
            <Button variant="ghost" onClick={closeDrawer} disabled={isSaving}>
              Cancelar
            </Button>
            <Button onClick={handleSubmit} loading={isSaving}>
              {editingPlan ? 'Salvar' : 'Criar plano'}
            </Button>
          </>
        }
      >
        <form onSubmit={handleSubmit} className="space-y-5">
          {/* Info basica */}
          <div className="space-y-4">
            <h4 className="text-xs font-semibold uppercase tracking-wider" style={{ color: '#8b5cf6' }}>
              Informacoes
            </h4>
            <FormField label="Nome" required>
              <Input
                value={form.name}
                onChange={(e) => setForm(f => ({ ...f, name: e.target.value }))}
                placeholder="Ex: Professional"
              />
            </FormField>
            <FormField label="Descricao">
              <Input
                value={form.description}
                onChange={(e) => setForm(f => ({ ...f, description: e.target.value }))}
                placeholder="Breve descricao do plano"
              />
            </FormField>
            <div className="grid grid-cols-2 gap-4">
              <FormField label="Status">
                <Select
                  value={form.status}
                  onChange={(value) => setForm(f => ({ ...f, status: value }))}
                  options={[
                    { value: 'active', label: 'Ativo' },
                    { value: 'inactive', label: 'Inativo' },
                  ]}
                />
              </FormField>
              <FormField label="Creditos por ciclo" required>
                <Input
                  type="number"
                  value={form.creditsPerCycle}
                  onChange={(e) => setForm(f => ({ ...f, creditsPerCycle: e.target.value }))}
                  placeholder="Ex: 10000"
                  min="1"
                  step="1"
                />
              </FormField>
            </div>
          </div>

          {/* Separator */}
          <div className="border-t" style={{ borderColor: '#e5e7eb' }} />

          {/* Precos */}
          <div className="space-y-4">
            <h4 className="text-xs font-semibold uppercase tracking-wider" style={{ color: '#8b5cf6' }}>
              Precos
            </h4>
            <div className="grid grid-cols-2 gap-4">
              <FormField label="Mensal (R$)" required>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  value={form.priceMonthly}
                  onChange={(e) => setForm(f => ({ ...f, priceMonthly: e.target.value }))}
                  placeholder="0.00"
                />
              </FormField>
              <FormField label="Anual (R$)">
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  value={form.priceYearly}
                  onChange={(e) => setForm(f => ({ ...f, priceYearly: e.target.value }))}
                  placeholder="0.00"
                />
              </FormField>
            </div>
            <FormField label="Moeda">
              <Select
                value={form.currency}
                onChange={(value) => setForm(f => ({ ...f, currency: value }))}
                options={[
                  { value: 'BRL', label: 'BRL (R$)' },
                  { value: 'USD', label: 'USD ($)' },
                ]}
              />
            </FormField>
          </div>

          {/* Separator */}
          <div className="border-t" style={{ borderColor: '#e5e7eb' }} />

          {/* Features */}
          <div className="space-y-4">
            <h4 className="text-xs font-semibold uppercase tracking-wider" style={{ color: '#8b5cf6' }}>
              Funcionalidades
            </h4>
            <div className="flex gap-2">
              <Input
                value={featureInput}
                onChange={(e) => setFeatureInput(e.target.value)}
                placeholder="Ex: Suporte prioritario"
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleAddFeature();
                  }
                }}
              />
              <Button type="button" variant="secondary" onClick={handleAddFeature}>
                Adicionar
              </Button>
            </div>
            {form.features.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {form.features.map((feature) => (
                  <span
                    key={feature}
                    className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium"
                    style={{
                      backgroundColor: 'rgba(139, 92, 246, 0.1)',
                      color: '#8b5cf6',
                    }}
                  >
                    {feature}
                    <button
                      type="button"
                      onClick={() => handleRemoveFeature(feature)}
                      className="ml-0.5 hover:opacity-70"
                    >
                      <FiX className="h-3 w-3" />
                    </button>
                  </span>
                ))}
              </div>
            )}
            {form.features.length === 0 && (
              <p className="text-xs" style={{ color: '#9ca3af' }}>
                Nenhuma funcionalidade adicionada. Estas aparecem no card do plano para o cliente.
              </p>
            )}
          </div>
        </form>
      </Drawer>
    </div>
  );
};
